/**
 * @fileoverview AI Decision Test Suites
 *
 * Migrated from Jest tests in test/zodiac-engine/ai/ai.test.ts and sentinelAI.test.ts.
 * Each test verifies the AI picks the correct destination for a given board position.
 *
 * All FENs use compact notation parsed by loadTestScenarioFEN / FenLoader.
 * Most tests have Dark as the AI side (matching the original Jest tests).
 */

import { AIDecisionTest, AIDecisionSuite, AIForcedLineTest } from "../AIDecisionTypes";

// === King Race Tests (from ai.test.ts "KingRace Tests") ===
const kingRaceTests: AIDecisionTest[] = [
  {
    name: "AI will choose first race win option",
    description: "Dark king can race to row 0 for immediate victory",
    inputFEN: "8/1bk6/6OK1/8/8",
    expectedDestination: { row: 0, col: 0 },
    side: "dark",
  },
  {
    name: "Race - capture resist",
    description: "AI avoids capturing enemy king and prefers guaranteed race victory",
    inputFEN: "8/R*7/1mk6/2MK5/8",
    expectedDestination: { row: 0, col: 2 },
    side: "dark",
  },
  {
    name: "Race - risk capture avoidance",
    description: "AI avoids Bear capture risk even though it is sequentially first",
    inputFEN: "8/R*7/BKmk6/8/8",
    expectedDestination: { row: 0, col: 3 },
    side: "dark",
  },
];

// === Kingslayer Test (from ai.test.ts "Kingslayer") ===
const kingslayerTests: AIDecisionTest[] = [
  {
    name: "AI can capture win",
    description: "Will force kingslayer if no race is possible in 1 ply",
    inputFEN: "8/5bkCK1/8/8/8",
    expectedDestination: { row: 6, col: 1 },
    side: "dark",
  },
];

// === Race Block Test (from ai.test.ts "Race block") ===
const raceBlockTests: AIDecisionTest[] = [
  {
    name: "Bear should block light race victory",
    description: "AI moves Bear to block enemy Crane king's immediate race win",
    inputFEN: "7c/5CK1wk/7c/6b1/8",
    expectedDestination: { row: 7, col: 3 },
    side: "dark",
  },
];

// === Don't Unblock Test (from ai.test.ts "Don't unblock") ===
const dontUnblockTests: AIDecisionTest[] = [
  {
    name: "Bear and wolf should not unblock light race victory",
    description: "AI will not unblock the light race victory even for a free capture",
    inputFEN: "8/5CK1wk/7c/7b/8",
    expectedDestination: { row: 6, col: 2 },
    side: "dark",
  },
];

// === KingRace Disrupt Tests (from ai.test.ts "KingRace Disrupt") ===
const kingRaceDisruptTests: AIDecisionTest[] = [
  {
    name: "AI will move pieces to block enemy race victory",
    description: "Bear should move out of monkey triple jump line to block enemy king race",
    inputFEN: "8/8/8/1MKG1C1bk1/7c",
    expectedDestination: { row: 5, col: 2 },
    side: "dark",
  },
  {
    name: "Kingrace Disrupt 2",
    description: "Bear should move out of jump line even though it has a free capture",
    inputFEN: "8/8/8/1MKG1bk1C1/7c",
    expectedDestination: { row: 3, col: 2 },
    side: "dark",
  },
  {
    name: "Kingrace Disrupt 3",
    description: "Should slide tree completely out of the way to block goat traverse immediate victory",
    inputFEN: "8/8/3MS3/2GK1btkC1/7c",
    expectedDestination: { row: 5, col: 0 },
    side: "dark",
  },
  {
    name: "Kingrace Disrupt 4",
    description: "Should prioritize blocking Rhino king race in 1 over enemy Monkey king race in 2",
    inputFEN: "8/4RK1b1/2mk5/8/8",
    expectedDestination: { row: 5, col: 0 },
    side: "dark",
  },
];

// === AI King Safety Tests (from ai.test.ts "AI King safety") ===
const kingSafetyTests: AIDecisionTest[] = [
  {
    name: "AI will move king out of danger before other moves",
    description: "AI should move Monkey king out of harm's way instead of getting a free capture",
    inputFEN: "6Cc/8/2BK/3mk/8",
    expectedDestination: { row: 4, col: 3 },
    side: "dark",
  },
  {
    name: "Goat king should recognize immediate bear threat",
    description: "Dark goat king at [5,6] in immediate danger from Light bear at [6,5]. Should move to safety",
    inputFEN: "8/5BKc1/6gk1/6c1/8",
    expectedDestination: { row: 5, col: 3 },
    side: "dark",
  },
];

// === Two Turn Kingrace Tests (from ai.test.ts "Two turn kingrace scenarios") ===
const twoTurnKingraceTests: AIDecisionTest[] = [
  {
    name: "AI prioritizes 2 turn unstoppable race victories",
    description: "AI should move crane king to a next turn race win instead of getting a free capture",
    inputFEN: "6Bb/8/WK2ck4/8/8",
    expectedDestination: { row: 1, col: 2 },
    side: "dark",
  },
  {
    name: "AI prioritizes slight risk for definite race victory in 2 turns",
    description: "AI should move crane king to a next turn race win (1/6 bear risk) instead of free capture",
    inputFEN: "8/2W5/1BK1mk4/8/8",
    expectedDestination: { row: 1, col: 0 },
    side: "dark",
  },
];

// === Sentinel Combo Tests (from sentinelAI.test.ts) ===
const sentinelComboTests: AIDecisionTest[] = [
  {
    name: "Virus infection chain to capture bear king",
    description: "Virus at [5,3] stacked on Crane must infect Bear King at [3,3] — forced win sequence",
    inputFEN: "d8/T2r3sk/5f2/3BK1C+v/U6b",
    expectedDestination: { row: 3, col: 3 },
    expectedMoveType: "Infect",
    side: "dark",
  },
  {
    name: "don't kill my own team test",
    description: "Copied first move from multiplayer game",
    inputFEN: "7w/7v/UK6rk✳️○/7b/7g",
    expectedDestination: { row: 3, col: 3 },
    expectedMoveType: "Infect",
    side: "light",
  },
  {
    name: "I hate my own king test",
    description: "Copied first move from multiplayer game",
    inputFEN: "U6u/3wk4/C1B5/5R✳️1s5/WK6g+v",
    expectedDestination: { row: 3, col: 3 },
    expectedMoveType: "Move",
    side: "dark",
  },
  {
    name: "Monkey consume-swing-capture combo",
    description: "Dark monkey B6 consumes fungi at C6 — starts a consume->swing->capture combo to win",
    inputFEN: "d5sk2/5m1t/G4u1f*/F*2MKT#3/S*7",
    expectedDestination: { row: 5, col: 2 },
    expectedMoveType: "Consume",
    side: "dark",
  },
];

// === Blunder Avoidance Tests ===
const blunderAvoidanceTests: AIDecisionTest[] = [
  {
    name: "Wolf should not walk into Monkey swing capture at B2",
    description: "Dark Wolf at A4 must avoid B2 — Light Monkey at E3 can swing from Tree D3 to capture it next turn. Wolf is not king, no reason to push forward into danger.",
    inputFEN: "dB2w4/U6t+o/CK7/2T4bk/2M4g",
    forbiddenDestinations: [{ row: 1, col: 1 }], // B2
    side: "dark",
  },
];

// === Suicidal King Tests (from multiplayer Drone blunders) ===
const suicidalKingTests: AIDecisionTest[] = [
  {
    name: "Crane king must not move into Wolf+Monkey capture at D7",
    description:
      "Light Crane King at B5 moved to D7 — immediately capturable by Dark Wolf at D8 and Dark Monkey at E8. " +
      "Reproduced from multiplayer game where Drone (Sentinel+Fast) self-sacrificed the king.",
    inputFEN: "G5ck1/4CK1b1/P↓6r✳️○/V6w/U6m",
    forbiddenDestinations: [{ row: 6, col: 3 }], // D7 = instant death
    side: "light",
  },
];

// === Hanging Monkey Tests ===
// Regression: Dark Monkey must not swing down column C into the Light Monkey's
// multi-turn (swing + bonus) capture range. AI search treats swing and bonus
// as separate plies, so without this test the AI may not look deep enough to
// avoid the trap.
const hangingMonkeyTests: AIDecisionTest[] = [
  {
    name: "AI must not hang Monkey to multi-turn swing counter",
    description:
      "Dark Monkey at C8 should avoid swinging down column C, where Light Monkey can capture via swing + bonus move (C2→C4 single swing reaches C4; C2→C4 swing then C4→C6 bonus leap reaches C6).",
    inputFEN: "8/8/1MKT3tkm/8/8",
    forbiddenDestinations: [
      { row: 3, col: 2 }, // C4 — captured by Light single swing
      { row: 5, col: 2 }, // C6 — captured by Light swing + bonus leap
    ],
    side: "dark",
  },
];

// === Export all suites ===
export const ALL_AI_DECISION_SUITES: AIDecisionSuite[] = [
  {
    name: "King Race",
    description: "AI correctly identifies and executes race victory moves",
    tests: kingRaceTests,
  },
  {
    name: "Kingslayer",
    description: "AI captures enemy king when no race is possible",
    tests: kingslayerTests,
  },
  {
    name: "Race Block",
    description: "AI blocks opponent's race victory",
    tests: raceBlockTests,
  },
  {
    name: "Don't Unblock",
    description: "AI avoids moves that would enable opponent's race victory",
    tests: dontUnblockTests,
  },
  {
    name: "KingRace Disrupt",
    description: "AI disrupts opponent's race path by repositioning pieces",
    tests: kingRaceDisruptTests,
  },
  {
    name: "King Safety",
    description: "AI prioritizes moving its king out of danger",
    tests: kingSafetyTests,
  },
  {
    name: "Two Turn Race",
    description: "AI identifies and executes multi-turn race victories",
    tests: twoTurnKingraceTests,
  },
  {
    name: "Sentinel Combos",
    description: "Complex multi-move combo sequences (Sentinel-specific)",
    tests: sentinelComboTests,
  },
  {
    name: "Blunder Avoidance",
    description: "AI avoids moves that walk pieces into immediate capture",
    tests: blunderAvoidanceTests,
  },
  {
    name: "Suicidal King",
    description: "AI must not sacrifice its own king by moving into immediate capture",
    tests: suicidalKingTests,
  },
  {
    name: "Multiturn Decision Making",
    description: "AI must not hang pieces by moving into opponent multi-turn (swing + bonus) capture range",
    tests: hangingMonkeyTests,
  },
];

/** Flat list of all AI decision tests */
export const ALL_AI_DECISION_TESTS: AIDecisionTest[] =
  ALL_AI_DECISION_SUITES.flatMap((s) => s.tests);

// === Human Instinct Line Tests (forced move sequences) ===
export const FORCED_LINE_TESTS: AIForcedLineTest[] = [
  {
    name: "Human instinct line test",
    description: "Human forces Light moves, AI responds as Dark. Bear block defense.",
    inputFEN: "8/5CK1wk/7c/6b1/8",
    side: "light",
    lightKing: "Wolf",
    darkKing: "Crane",
    forcedMoves: [
      { from: { row: 1, col: 3 }, to: { row: 0, col: 3 } }, // Bear D2→D1
    ],
  },
];
